import { useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import { orDash } from '../../../../utils/schema-parse';
import type { ResumoMapa, ResumoTipoView } from './resumo-view.types';
import MapasInfestacaoModal from './mapas-infestacao-modal';
import ResumoOcorrenciasGrid from './resumo-ocorrencias-grid';

interface ResumoSectionProps {
  resumo: ResumoTipoView;
  mapasInfestacao: ResumoMapa[];
  tituloTalhao: string;
}

const ResumoSection = ({
  resumo,
  mapasInfestacao,
  tituloTalhao,
}: ResumoSectionProps) => {
  const [infestacaoOpen, setInfestacaoOpen] = useState(false);

  const tabelas = useMemo(
    () =>
      [...resumo.tabelas].sort((a, b) =>
        orDash(a.titulo).localeCompare(orDash(b.titulo), 'pt-BR'),
      ),
    [resumo.tabelas],
  );

  const grid = resumo.layout === 'grid_cards';
  const vazio = grid ? resumo.cards.length === 0 : tabelas.length === 0;

  if (vazio && mapasInfestacao.length === 0) return null;

  return (
    <section className="rv-resumo">
      <div className="rv-resumo-header">
        {resumo.iconeUrl ? (
          <img
            alt=""
            className="rv-resumo-icon-img"
            height={20}
            src={resumo.iconeUrl}
            width={20}
          />
        ) : null}
        <h3 className="rv-resumo-title">{orDash(resumo.titulo)}</h3>
        {mapasInfestacao.length > 0 ? (
          <button
            type="button"
            className="rv-resumo-infest-btn"
            onClick={() => setInfestacaoOpen(true)}
          >
            <Layers size={15} />
            Ver Mapas de Infestação
          </button>
        ) : null}
      </div>
      {resumo.qtdPontos != null && resumo.showQtdPontos ? (
        <p className="rv-resumo-qtd">(Qtd. Pontos: {resumo.qtdPontos})</p>
      ) : null}

      {grid ? (
        <ResumoOcorrenciasGrid cards={resumo.cards} />
      ) : (
        <div className="rv-resumo-tables">
          {tabelas.map((tabela) => (
            <div
              key={`${tabela.ordem}-${tabela.titulo}-${
                tabela.mapaInfestacao ?? ''
              }`}
              className="rv-resumo-card"
            >
              <div className="rv-resumo-card-title">
                {orDash(tabela.titulo)}
              </div>
              <div className="rv-linhas-table rv-resumo-scroll">
                <table
                  className="rv-resumo-table"
                  style={{ tableLayout: 'fixed' }}
                >
                  <colgroup>
                    {(tabela.colunas ?? []).map((coluna) => (
                      <col
                        key={coluna}
                        style={{
                          width: `${100 / (tabela.colunas?.length || 1)}%`,
                        }}
                      />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {(tabela.colunas ?? []).map((coluna) => (
                        <th key={coluna} className="rv-resumo-th">
                          {orDash(coluna)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(tabela.valores ?? []).map((valor) => (
                      <tr key={valor.ordem} className="rv-resumo-row">
                        {(valor.linha ?? []).map((celula) => (
                          <td
                            key={celula.ordem}
                            className="rv-resumo-cell"
                            style={{
                              fontWeight: celula.cor ? 600 : 400,
                              color:
                                celula.cor ?? 'rgb(var(--color-aqila-body))',
                            }}
                          >
                            {orDash(celula.valor)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rv-linhas-mobile rv-resumo-mobile">
                {(tabela.valores ?? []).map((valor) => (
                  <div key={valor.ordem} className="rv-resumo-m-row">
                    {(valor.linha ?? []).map((celula, celulaIndex) => (
                      <div key={celula.ordem} className="rv-resumo-m-field">
                        <span className="rv-resumo-m-label">
                          {orDash(tabela.colunas?.[celulaIndex])}
                        </span>
                        <span
                          className="rv-resumo-m-value"
                          style={{
                            fontWeight: celula.cor ? 600 : 400,
                            color: celula.cor ?? 'rgb(var(--color-aqila-body))',
                          }}
                        >
                          {orDash(celula.valor)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <MapasInfestacaoModal
        mapas={mapasInfestacao}
        open={infestacaoOpen}
        titulo={tituloTalhao}
        onClose={() => setInfestacaoOpen(false)}
      />
    </section>
  );
};

export default ResumoSection;
